import React from "react";

export interface TabItem<T extends string = string> {
  id: T;
  label: string;
  /** Omit on tabs that carry no count badge, e.g. the brand setup screen. */
  count?: number;
  /**
   * Whether the list behind this tab is holding something nobody has looked at
   * yet — a dot beside the label, in the same red the rail badges with.
   *
   * A dot rather than a second number, because the count beside it is already
   * a number and the two would be read as one figure. What is being said here
   * is "there is something in here", which a dot says at a glance and without
   * asking anybody to do arithmetic.
   */
  dot?: boolean;
}

interface TabsProps<T extends string> {
  tabs: TabItem<T>[];
  activeTab: T;
  onChange: (tab: T) => void;
}

// Generic over the tab id so each screen keeps its own union of tab names
// instead of widening to `string`.
function Tabs<T extends string>({ tabs, activeTab, onChange }: TabsProps<T>) {
  return (
    <div role="tablist" className="flex items-center gap-6">
      {tabs.map((tab) => {
        const active = tab.id === activeTab;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.id)}
            className={`relative flex items-center gap-2 pb-3 text-[14px] 3xl:text-[16px] transition-colors ${
              active ? "text-[#00BACB] font-medium" : "text-ink"
            }`}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span
                className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-medium text-white ${
                  active ? "bg-[#00BACB]" : "bg-[#00424B]"
                }`}
              >
                {tab.count}
              </span>
            )}
            {/* Not `role="status"`: the tab is a control that happens to be
                carrying a mark, not news announcing itself, and a row of them
                speaking up on every render is noise. The words are here for a
                screen reader to find when the tab is read. */}
            {tab.dot && (
              <>
                <span className="sr-only">has something new</span>
                <span
                  aria-hidden="true"
                  className="w-1.5 h-1.5 rounded-full bg-[#FF6158] shadow-[0_0_0_3px_#FF61581F]"
                />
              </>
            )}
            {/* Sits on top of the parent's bottom border rather than replacing
                it, so switching tabs doesn't shift the row by a pixel. */}
            {active && (
              <span
                aria-hidden="true"
                className="absolute inset-x-0 -bottom-px h-[2px] rounded-full bg-brand"
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

export default Tabs;
